/**
 * Abstraction over browser storage so the rest of the app never touches
 * `localStorage`/`sessionStorage` directly. Swapping the backing store
 * (e.g. to an in-memory store, IndexedDB, or a secure native bridge) only
 * requires a new implementation of this interface.
 */
export interface StorageService {
  getItem<T = string>(key: string): T | null;
  setItem<T = string>(key: string, value: T): void;
  removeItem(key: string): void;
  clear(): void;
}

class WebStorageService implements StorageService {
  constructor(private readonly backend: Storage) {}

  getItem<T = string>(key: string): T | null {
    const raw = this.backend.getItem(key);
    if (raw === null) return null;

    try {
      return JSON.parse(raw) as T;
    } catch {
      // Value was stored as a plain string (not JSON-encoded).
      return raw as unknown as T;
    }
  }

  setItem<T = string>(key: string, value: T): void {
    const raw = typeof value === 'string' ? value : JSON.stringify(value);
    this.backend.setItem(key, raw);
  }

  removeItem(key: string): void {
    this.backend.removeItem(key);
  }

  clear(): void {
    this.backend.clear();
  }
}

export const localStorageService: StorageService = new WebStorageService(window.localStorage);
export const sessionStorageService: StorageService = new WebStorageService(window.sessionStorage);

/**
 * Minimal string-in/string-out storage contract (matches Zustand's
 * `PersistStorage`/`StateStorage` shape) for callers - like the Zustand
 * persist middleware - that already serialize their own payload and must
 * not have it re-parsed by `StorageService.getItem`. Still routes through
 * this module so raw `window.localStorage` access stays confined here.
 */
export interface RawStringStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const localRawStorage: RawStringStorage = window.localStorage;
